/**
 * IndexedDB Drafts Service (ported from Noteble's capture flow — ADR-022).
 * The LOCAL half of the /add draft store.
 *
 * Since ADR-030 §9 a draft is also written to a tenant-scoped server buffer
 * (`captureBuffersApi`), which is what lets an in-progress capture survive a
 * device change. This store did not become a cache: it holds the photo BYTES,
 * which never go to the server (only the mediaId does), and it is the only
 * half that works with no network. `useDrafts` writes both and reads whichever
 * can answer completely.
 *
 * Blobs (photos) are stored inline here — no presigned URLs or S3 uploads
 * needed to keep a draft. An uploaded photo also keeps its tenant mediaId, so
 * a restore can reuse it and the server buffer has something to reference.
 */

import { createLogger } from '@/lib/logger';

const log = createLogger('indexedDBDraftsService');

const DB_NAME = 'systemdox-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface LocalDraftNote {
  id: string;
  text: string;
  timestamp: number;
  sourceType?: string;
  sourceTitle?: string;
}

export interface LocalDraftPhoto {
  blob: Blob;
  timestamp: number;
  description?: string;
  mediaId?: string;
  mermaid?: string | null;
}

export interface LocalDraft {
  draftId: string;
  title: string;
  inputMethod: string;
  languageCode: string;
  templateIds?: string[];
  createdAt: number;
  updatedAt: number;
  items: {
    photos: LocalDraftPhoto[];
    notes: LocalDraftNote[];
    streamingTranscripts: Array<{ transcript: string; duration: number }>;
  };
}

export interface DraftSummary {
  id: string;
  title: string;
  updatedAt: string;
  inputMethod: string;
  photoCount: number;
  noteCount: number;
  streamingTranscriptCount: number;
}

class IndexedDBDraftsService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isClosing = false;

  private isDatabaseAvailable(): boolean {
    return this.db !== null && !this.isClosing;
  }

  private async init(): Promise<void> {
    if (this.db && !this.isClosing) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        log.error('Failed to open IndexedDB', request.error as unknown as Error);
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isClosing = false;
        this.db.onclose = () => {
          this.isClosing = true;
          log.info('IndexedDB drafts connection closed');
        };
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'draftId' });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
          log.info('Created drafts object store');
        }
      };
    });

    return this.initPromise;
  }

  async saveDraft(draft: LocalDraft): Promise<void> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return;

      const tx = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.put(draft);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to save draft', err as Error);
      throw err;
    }
  }

  async getDraft(draftId: string): Promise<LocalDraft | null> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return null;

      const tx = this.db!.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.get(draftId);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to get draft', err as Error);
      return null;
    }
  }

  async listDrafts(): Promise<DraftSummary[]> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return [];

      const tx = this.db!.transaction([STORE_NAME], 'readonly');
      const store = tx.objectStore(STORE_NAME);

      const all = await new Promise<LocalDraft[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      const now = Date.now();
      const valid = all.filter((d) => now - d.createdAt < MAX_AGE_MS);
      const stale = all.filter((d) => now - d.createdAt >= MAX_AGE_MS);

      if (stale.length > 0) {
        log.info('Cleaning up stale drafts', { count: stale.length });
        void Promise.all(stale.map((d) => this.deleteDraft(d.draftId)));
      }

      return valid
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((d) => ({
          id: d.draftId,
          title: d.title,
          updatedAt: new Date(d.updatedAt).toISOString(),
          inputMethod: d.inputMethod,
          photoCount: d.items.photos.length,
          noteCount: d.items.notes.length,
          streamingTranscriptCount: d.items.streamingTranscripts.length,
        }));
    } catch (err) {
      log.error('Failed to list drafts', err as Error);
      return [];
    }
  }

  async deleteDraft(draftId: string): Promise<void> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return;

      const tx = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(draftId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to delete draft', err as Error);
    }
  }

  async clearAll(): Promise<void> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return;

      const tx = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to clear drafts', err as Error);
    }
  }

  close(): void {
    if (this.db) {
      this.isClosing = true;
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }
}

export const indexedDBDraftsService = new IndexedDBDraftsService();
